import { __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { PasswordIndicator } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { customerMutations } from '@as-react/common-queries';
import { DefaultSettings, ErrorMessage, HTTPStatusCode } from '@as-react/common-types';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { Banner, Box, ButtonAlpha, Grid, Stack, TextAlpha, TextField } from '@yonderland/andes-react';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FormField } from '../components/form/FormField';
import { PASSWORD_MIN_LENGTH } from '../constants/FormConstants';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
const ChangePasswordContainer = ({ title }) => {
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const { t } = useTranslation(I18N_NAMESPACES.account);
    const { updatePassword: updatePasswordMutation } = customerMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { mutate: updatePassword, error, isSuccess } = useMutation(updatePasswordMutation());
    const changePasswordSchema = Yup.object().shape({
        oldPassword: Yup.string()
            .required(t(ErrorMessage.REQUIRED))
            .min(DefaultSettings.MIN_LENGTH_PASSWORD, t(ErrorMessage.ACCOUNT_MIN_LENGTH)),
        newPassword: Yup.string()
            .required(t(ErrorMessage.REQUIRED))
            .min(DefaultSettings.MIN_LENGTH_PASSWORD, t(ErrorMessage.ACCOUNT_MIN_LENGTH)),
        repeatPassword: Yup.string()
            .required(t(ErrorMessage.REQUIRED))
            .oneOf([Yup.ref('newPassword')], t(ErrorMessage.PASSWORD_MATCH)),
    });
    const initialValues = {
        oldPassword: '',
        newPassword: '',
        repeatPassword: '',
    };
    const formProps = useForm({
        defaultValues: initialValues,
        mode: 'onTouched',
        resolver: yupResolver(changePasswordSchema),
    });
    const errorFeedback = useMemo(() => {
        var _a;
        if (!error) {
            return;
        }
        if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === HTTPStatusCode.Unauthorized) {
            formProps.setError('oldPassword', { type: 'manual', message: t('account.password.current.invalid') });
        }
        else {
            return t('account.fail');
        }
    }, [error]);
    const handleSubmit = values => {
        updatePassword({
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
        }, {
            onSuccess: () => {
                formProps.reset();
            },
        });
    };
    const passwordIndicatorTranslations = {
        label: t(`${I18N_NAMESPACES.account}:password.indicator.strength.label`),
        text: value => t(`${I18N_NAMESPACES.account}:${value}`),
    };
    return (_jsxs(_Fragment, { children: [title.show && (_jsx(Box, { paddingBlockEnd: "4", marginInline: { base: '-4', md: '0' }, children: _jsx(TextAlpha, { as: "h2", variant: "subheadingM", children: title.value || t('account.updatePassword') }) })), _jsx(FormProvider, Object.assign({}, formProps, { children: _jsx("form", { onSubmit: formProps.handleSubmit(handleSubmit), noValidate: true, name: "update-password-form", children: _jsx(Grid, { columns: 12, children: _jsx(Grid.Cell, { span: { base: 12, md: 5 }, children: _jsxs(Stack, { direction: "col", gap: "6", children: [_jsx(FormField, { name: "oldPassword", required: true, children: (_a) => {
                                            var _b = _a.fieldProps, { ref } = _b, fieldProps = __rest(_b, ["ref"]), { error } = _a;
                                            return (_jsx(TextField, Object.assign({}, fieldProps, { innerRef: ref, label: t('account.password.passwordOld'), placeholder: t('account.password'), id: fieldProps.name, testId: fieldProps.name, error: error, type: "password", 
                                                // autoComplete="off"
                                                minLength: PASSWORD_MIN_LENGTH })));
                                        } }), _jsx("div", { children: _jsx(FormField, { name: "newPassword", required: true, children: (_a) => {
                                                var _b = _a.fieldProps, { ref } = _b, fieldProps = __rest(_b, ["ref"]), { error } = _a;
                                                return (_jsxs(_Fragment, { children: [_jsx(TextField, Object.assign({}, fieldProps, { innerRef: ref, label: t('account.password.passwordOne'), placeholder: t('account.password'), id: fieldProps.name, testId: fieldProps.name, error: error, type: "password", 
                                                            // autoComplete="off"
                                                            minLength: PASSWORD_MIN_LENGTH })), formProps.getValues('newPassword').length >= PASSWORD_MIN_LENGTH && (_jsx(PasswordIndicator, { password: fieldProps.value, translations: passwordIndicatorTranslations }))] }));
                                            } }) }), _jsx(FormField, { name: "repeatPassword", required: true, children: (_a) => {
                                            var _b = _a.fieldProps, { ref } = _b, fieldProps = __rest(_b, ["ref"]), { error } = _a;
                                            return (_jsx(TextField, Object.assign({}, fieldProps, { innerRef: ref, label: t('account.password.passwordTwo'), placeholder: t('account.password'), id: fieldProps.name, testId: fieldProps.name, error: error, type: "password", 
                                                // autoComplete="off"
                                                minLength: PASSWORD_MIN_LENGTH })));
                                        } }), errorFeedback && _jsx(Banner, { testId: "change-password-banner-error", status: "error", text: errorFeedback }), isSuccess && (_jsx(Banner, { testId: "change-password-banner-success", status: "success", text: t('account.password.finished.button.state') })), _jsx(ButtonAlpha, { type: "submit", state: formProps.formState.isSubmitting ? 'loading' : 'interactive', testId: "update-password-btn", children: t('account.details.updatepassword') })] }) }) }) }) }))] }));
};
export default ChangePasswordContainer;
